import React from 'react';
import type { StyletronComponent } from 'styletron-react';
import { createStyled, withStyle } from 'styletron-react'; // withStyle is not deprecated, withStyleDeep is, this is a mistake in the .d.ts file
// "withStyleDeep has been deprecated in v5 of Styletron. withStyle now performs a deep object merge by default as this was what was generally desired."
import type { StyleObject } from 'styletron-standard';
import { driver, getInitialStyle } from 'styletron-standard';

/**
 * The selected sidebar item's aurora. Its angle follows the shape it fills: 119deg on a full row,
 * flatter on the square collapsed button and the long hover pill, so the colours sweep the same way.
 */
export const sidebarSelectedGradient = (angleDeg: number): string =>
  `linear-gradient(${angleDeg}deg, rgba(131, 71, 242, 0.6) 0%, rgba(144, 223, 255, 0.28) 100%)`;

export const THEME = {
  colors: {
    black: '#000',
    white: '#fff',
    primary: '#0067F4',
    background: '#0a132b',
    green: '#1cc23a',
    greenHover: '#1fd242',
    defaultGray: '#B5BFD2',
    yellow: '#ffc600',
    teal: '#42B9BB',
    purple: '#A57AF4',
    red: '#E43482',
    clearButton: '#F02929',
    error: '#d4380d',
    orange: '#FCA95A',
    inactive: '#6c7280',
    backgroundGrey: '#f0f2f5',
    backgroundGrey40: 'rgba(240, 242, 245, 0.4)',
    componentBackground: '#f9fbfc',
    catalogIconBackground: '#fff5eb',
    catalogTableBackground: '#fff',
    catalogTableBorder: '#dadee6',
    catalogTableBorderHover: '#2267f4',
    catalogTableShadowHover: 'rgba(34,103,244,.2)',
    catalogTableRowTitle: '#a9adb5',
    catalogTableRowNameHover: '#0067F4',
    // The sidebar, after the "Midnight Aurora v2" Figma (s3qI6SB4dToKAKo4e6a2am, 89:3279): a
    // near-black workspace rail beside the #0a132b nav panel. Items are #c3c8e6 with #a3a8d6
    // icons, white on a 7% white surface when hovered, and the selection is an aurora pill
    // (`sidebarItemSelected*`) rather than a colour.
    sidebarRailBackground: '#01081a',
    sidebarBackground: '#0a132b',
    sidebarBorder: '#1a2440',
    sidebarDivider: '#2c3855',
    sidebarItem: '#c3c8e6',
    sidebarIcon: '#a3a8d6',
    sidebarMuted: '#8f99b0',
    sidebarItemHover: '#fff',
    sidebarItemHoverBackground: 'rgba(255, 255, 255, 0.07)',
    sidebarItemSelected: '#fff',
    sidebarItemSelectedBackground: sidebarSelectedGradient(119),
    sidebarItemSelectedBorder: 'rgba(196, 166, 255, 0.95)',
    // Kept within ~4px of the row: rows sit flush, and the Figma's 0 6px 11px glow spilled onto
    // the row below (BEEM-2362).
    sidebarItemSelectedShadow: '0 2px 6px rgba(131, 71, 242, 0.35)',
    sidebarGroupLabel: '#6c7a99',
    // The hover pill: opaque, because it draws over the page beside the sidebar.
    sidebarPillBackground: '#1b2439',
    sidebarSearchBackground: '#050d22',
    sidebarSearchBorder: '#1f2a47',
    sidebarFocus: '#97d7fe',
    // The app's semantic UI palette (BEEM Product Design System): text, lines and surfaces for
    // light content. New components take their colours from here rather than from literals.
    ui: {
      ink: '#0a132b', // titles and body text (midnight)
      inkSecondary: '#4f576d', // secondary text and labels
      inkMuted: '#7e8595', // captions, metadata
      inkSubtle: '#6b707f', // placeholder-weight text
      border: '#dadee6', // card and panel frames
      borderSubtle: '#eef0f4', // row dividers inside a panel
      controlBorder: '#d9dee7', // tags and secondary controls
      controlBorderHover: '#b6c0d3',
      surface: '#fff',
      surfaceHover: '#f8f8fa',
      surfaceTint: 'rgba(218, 222, 230, 0.2)', // header bands, wells
      primary: '#0067f4',
      accent: '#755afe', // halo cards' hover border
      focus: '#97d7fe',
    },
    avatarDropdownItemHover: '#2c78f5',
    avatarDropdownItemBackgroundHover: '#e5f0fe',
    avatarDropdownLogoutHover: '#f13736',
    avatarDropdownLogoutBackgroundHover: '#fde8e8',
    flowStateLive: '#87de97',
    flowStatePaused: '#bbbfc7',
    flowTableBackgroundPaused: '#f0f2f5',
    tabbedPagePrimary: '#0067f4',
    tabbedPageUnselectedTab: '#6d7281',
    tabbedPageBorder: '#dadee6',
    tabbedPagePrimaryBg: '#fff',
    tabbedPageSecondaryBg: '#f9fafb',
    selectLangShadow: '#cce1fd',
    selectLangBorderFocus: '#0067f4',
    integrationCardBg: '#fff',
    integrationCardBorder: '#dadee6',
    addIntegrationCard: '#0067f4',
    integrationDetailsBg: '#f4f5f7',
    integrationDetailsBorder: '#dadee6',
    integrationSuccessBorder: '#dadfe8',
    dropdownBorder: '#dadee6',
    widgetBorder: '#040A1A',
    siderBackground: '#232b41',
    siderDarkBackground: '#313951',
    alerts: {
      warningBorder: '#ffA800',
      warningBackground: '#fdeecc',
    },
    progressBar: {
      background: '#010818',
    },
    dataTable: {
      background: '#F8F9FB99',
      shadow: '#FFFFFFB3',
    },
    dashboard: {
      solid: {
        purple: '#8347F2',
        blue: '#0067F4',
        cyan: '#38C9FA',
        green: '#1CC23A',
        red: '#F4217E',
        orange: '#E43437',
        lightOrange: '#E47D34',
        yellow: '#E4CF34',
      },
      lineChart: {
        gradient: {
          top: {
            purple: '#8347F266',
            pink: '#F34DC166',
            blue: '#1EB6E966',
            orange: '#E47D3466',
            cyan: '#38C9FA66',
            green: '#48C21C66',
            red: '#E43A3466',
            yellow: '#E4CF3466',
          },
          bottom: {
            purple: '#4A34F20D',
            pink: '#F4217E0D',
            blue: '#0067F40D',
            orange: '#E440340D',
            cyan: '#1BD3C10D',
            green: '#1CC23A0D',
            red: '#F4214B0D',
            yellow: '#E49B340D',
          },
        },
      },
      gradient: {
        top: {
          purple: '#8347F2CC',
          pink: '#F34DC1CC',
          blue: '#1EB6E9CC',
          orange: '#E47D34CC',
          cyan: '#38C9FACC',
          green: '#48C21CCC',
          red: '#E43A34CC',
          yellow: '#E4CF34CC',
        },
        bottom: {
          purple: '#4A34F2CC',
          pink: '#F4217ECC',
          blue: '#0067F4CC',
          orange: '#E44034CC',
          cyan: '#1BD3C1CC',
          green: '#1CC23ACC',
          red: '#F4214BCC',
          yellow: '#E49B34CC',
        },
      },
    },
  },
  transitions: {
    hoverEffect: '0.3s',
  },
  textColor: {
    grey: '#6B707F',
    black: '#0A132B',
  },
};

const ThemeContext = React.createContext(THEME);

const ThemeProvider = ({ children }: { children: React.ComponentType }) => (
  <ThemeContext.Provider value={THEME}>{children}</ThemeContext.Provider>
);

const wrapper = <P extends Record<string, unknown>>(
  StyledComponent: React.ComponentType<P>,
): React.ComponentType<P> =>
  function withThemeHOC(props: P) {
    return (
      <ThemeContext.Consumer>
        {(theme) => <StyledComponent {...props} $theme={theme} />}
      </ThemeContext.Consumer>
    );
  };

const styled = createStyled({ wrapper, getInitialStyle, driver }) as {
  <
    C extends keyof JSX.IntrinsicElements | React.ComponentType<any>,
    P extends Record<string, unknown>,
  >(
    component: C,
    style: (props: P & { $theme: typeof THEME }) => StyleObject,
  ): StyletronComponent<
    Pick<React.ComponentProps<C>, Exclude<keyof React.ComponentProps<C>, { className: string }>> & P
  >;
  <C extends keyof JSX.IntrinsicElements | React.ComponentType<any>>(
    component: C,
    style: StyleObject,
  ): StyletronComponent<
    Pick<React.ComponentProps<C>, Exclude<keyof React.ComponentProps<C>, { className: string }>>
  >;
};

export { ThemeContext, ThemeProvider, styled, withStyle };
